import React, { useState, useEffect, useMemo } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    flexRender,
} from '@tanstack/react-table';
import EditModal from './EditModal';
import '../../css/AdminTable.css';
import { BACKEND_URL } from '../../config';

/**
 * CarsAdmin - Компонент для управління марками та моделями автомобілів
 * 
 * Містить два таби:
 * 1. Марки (Brands) - CRUD для автомобільних марок
 * 2. Моделі (Models) - CRUD для моделей автомобілів з прив'язкою до марки
 */
// base64-encoded confirmation phrase (not stored as plain text)
const CONFIRM_HASH = 'ZGVsZXRlYWxs';

const CarsAdmin = () => {
    // Стан для перемикання табів
    const [activeTab, setActiveTab] = useState('brands'); // 'brands' або 'models'

    // Стани для марок
    const [brandsData, setBrandsData] = useState([]);
    const [originalBrandsMap, setOriginalBrandsMap] = useState({});
    
    // Стани для моделей
    const [modelsData, setModelsData] = useState([]);
    const [originalModelsMap, setOriginalModelsMap] = useState({});

    // Загальні стани
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Стан для модального вікна "Видалити всі бренди"
    const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
    const [deleteAllPassword, setDeleteAllPassword] = useState('');
    const [deleteAllLoading, setDeleteAllLoading] = useState(false);
    const [deleteAllError, setDeleteAllError] = useState('');

    // Стан для модального вікна
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editModalData, setEditModalData] = useState({
        value: null,
        rowIndex: null,
        columnId: null,
        fieldType: 'text',
        fieldLabel: ''
    });

    // ============= Завантаження даних =============

    // Завантаження марок
    useEffect(() => {
        fetchBrands();
    }, []);

    // Завантаження моделей при переключенні на таб моделей
    useEffect(() => {
        if (activeTab === 'models') {
            fetchModels();
        }
    }, [activeTab]);

    const fetchBrands = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${BACKEND_URL}/cars/brands`);
            if (!response.ok) throw new Error(`HTTP помилка! Статус: ${response.status}`);
            const data = await response.json();
            setBrandsData(data || []);
            setOriginalBrandsMap(data.reduce((map, item) => ({ ...map, [item.id]: item }), {}));
            setError(null);
        } catch (err) {
            console.error('Помилка завантаження марок:', err);
            setError(err.message);
            setBrandsData([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchModels = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${BACKEND_URL}/cars/models`);
            if (!response.ok) throw new Error(`HTTP помилка! Статус: ${response.status}`);
            const data = await response.json();
            setModelsData(data || []);
            setOriginalModelsMap(data.reduce((map, item) => ({ ...map, [item.id]: item }), {}));
            setError(null);
        } catch (err) {
            console.error('Помилка завантаження моделей:', err);
            setError(err.message);
            setModelsData([]);
        } finally {
            setLoading(false);
        }
    };

    // ============= МАРКИ - CRUD операції =============

    const openCreateBrandModal = () => {
        setEditModalData({
            value: { name: '' },
            rowIndex: null,
            columnId: null,
            fieldType: 'createBrand',
            fieldLabel: 'Створити нову марку'
        });
        setIsModalOpen(true);
    };

    const handleCreateBrand = async (brandData) => {
        setLoading(true);
        setError(null);
        try {
            const authToken = localStorage.getItem('authToken');
            const response = await fetch(`${BACKEND_URL}/cars/brands`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
                },
                body: JSON.stringify(brandData),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP помилка! Статус: ${response.status}, Текст: ${errorText || 'N/A'}`);
            }

            const newBrand = await response.json();
            setBrandsData(prev => [...prev, newBrand]);
            setOriginalBrandsMap(prev => ({ ...prev, [newBrand.id]: newBrand }));
        } catch (err) {
            console.error('Помилка створення марки:', err);
            setError(`Помилка створення марки: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveBrand = async (rowData) => {
        setLoading(true);
        setError(null);
        try {
            const authToken = localStorage.getItem('authToken');
            const response = await fetch(`${BACKEND_URL}/cars/brands/${rowData.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
                },
                body: JSON.stringify({ name: rowData.name }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP помилка! Статус: ${response.status}, Текст: ${errorText || 'N/A'}`);
            }

            const updatedBrand = await response.json();
            setOriginalBrandsMap(prev => ({ ...prev, [updatedBrand.id]: updatedBrand }));
            setBrandsData(prev => prev.map(item => item.id === updatedBrand.id ? updatedBrand : item));
        } catch (err) {
            console.error('Помилка оновлення марки:', err);
            setError(`Помилка оновлення марки: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteBrand = async (brandId) => {
        setLoading(true);
        setError(null);
        try {
            const authToken = localStorage.getItem('authToken');
            const response = await fetch(`${BACKEND_URL}/cars/brands/${brandId}`, {
                method: 'DELETE',
                headers: {
                    ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP помилка! Статус: ${response.status}, Текст: ${errorText || 'N/A'}`);
            }

            setBrandsData(prev => prev.filter(item => item.id !== brandId));
            setOriginalBrandsMap(prev => {
                const { [brandId]: _, ...rest } = prev;
                return rest;
            });
        } catch (err) {
            console.error('Помилка видалення марки:', err);
            setError(`Помилка видалення марки: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // ============= МОДЕЛІ - CRUD операції =============

    const openCreateModelModal = () => {
        setEditModalData({
            value: { name: '', brandId: brandsData[0]?.id || null },
            rowIndex: null,
            columnId: null,
            fieldType: 'createModel',
            fieldLabel: 'Створити нову модель',
            brands: brandsData // Передаємо список марок для селекту
        });
        setIsModalOpen(true);
    };

    const handleCreateModel = async (modelData) => {
        setLoading(true);
        setError(null);
        try {
            const authToken = localStorage.getItem('authToken');
            const response = await fetch(`${BACKEND_URL}/cars/models`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
                },
                body: JSON.stringify(modelData),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP помилка! Статус: ${response.status}, Текст: ${errorText || 'N/A'}`);
            }

            const newModel = await response.json();
            setModelsData(prev => [...prev, newModel]);
            setOriginalModelsMap(prev => ({ ...prev, [newModel.id]: newModel }));
        } catch (err) {
            console.error('Помилка створення моделі:', err);
            setError(`Помилка створення моделі: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveModel = async (rowData) => {
        setLoading(true);
        setError(null);
        try {
            const authToken = localStorage.getItem('authToken');
            const response = await fetch(`${BACKEND_URL}/cars/models/${rowData.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
                },
                body: JSON.stringify({
                    name: rowData.name,
                    brandId: rowData.brandId
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP помилка! Статус: ${response.status}, Текст: ${errorText || 'N/A'}`);
            }

            const updatedModel = await response.json();
            setOriginalModelsMap(prev => ({ ...prev, [updatedModel.id]: updatedModel }));
            setModelsData(prev => prev.map(item => item.id === updatedModel.id ? updatedModel : item));
        } catch (err) {
            console.error('Помилка оновлення моделі:', err);
            setError(`Помилка оновлення моделі: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteModel = async (modelId) => {
        setLoading(true);
        setError(null);
        try {
            const authToken = localStorage.getItem('authToken');
            const response = await fetch(`${BACKEND_URL}/cars/models/${modelId}`, {
                method: 'DELETE',
                headers: {
                    ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP помилка! Статус: ${response.status}, Текст: ${errorText || 'N/A'}`);
            }

            setModelsData(prev => prev.filter(item => item.id !== modelId));
            setOriginalModelsMap(prev => {
                const { [modelId]: _, ...rest } = prev;
                return rest;
            });
        } catch (err) {
            console.error('Помилка видалення моделі:', err);
            setError(`Помилка видалення моделі: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // ============= Обробка модального вікна =============

    const handleModalSave = (newValue) => {
        const { rowIndex, columnId, fieldType } = editModalData;

        if (fieldType === 'createBrand') {
            handleCreateBrand(newValue);
        } else if (fieldType === 'createModel') {
            handleCreateModel(newValue);
        } else if (rowIndex !== null && columnId !== null) {
            // Редагування існуючого рядка
            if (activeTab === 'brands') {
                brandsTable.options.meta?.updateData(rowIndex, columnId, newValue);
            } else {
                modelsTable.options.meta?.updateData(rowIndex, columnId, newValue);
            }
        }
    };

    // ============= Таблиця для МАРОК =============

    const brandsColumns = useMemo(() => [
        {
            accessorKey: 'id',
            header: 'ID',
            size: 80,
            enableEditing: false,
            cell: info => info.getValue(),
        },
        {
            accessorKey: 'name',
            header: 'Назва марки',
            size: 300,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>,
        },
        {
            id: 'actions',
            header: 'Дії',
            size: 120,
            cell: ({ row }) => {
                const originalRow = originalBrandsMap[row.original.id];
                const hasChanged = originalRow && JSON.stringify(originalRow) !== JSON.stringify(row.original);
                return (
                    <div className="action-buttons">
                        <button
                            onClick={() => handleSaveBrand(row.original)}
                            className="save-button"
                            disabled={!hasChanged}
                        >
                            Зберегти
                        </button>
                        <button
                            onClick={() => handleDeleteBrand(row.original.id)}
                            className="delete-button icon-button"
                            title="Видалити"
                        >
                            ×
                        </button>
                    </div>
                );
            },
        },
    ], [originalBrandsMap]);

    const brandsTable = useReactTable({
        data: brandsData,
        columns: brandsColumns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: { pageSize: 50 },
        },
        meta: {
            updateData: (rowIndex, columnId, value) => {
                setBrandsData(old =>
                    old.map((row, index) => {
                        if (index === rowIndex) {
                            return { ...row, [columnId]: value };
                        }
                        return row;
                    })
                );
            },
        },
    });

    // ============= Таблиця для МОДЕЛЕЙ =============

    const modelsColumns = useMemo(() => [
        {
            accessorKey: 'id',
            header: 'ID',
            size: 80,
            enableEditing: false,
            cell: info => info.getValue(),
        },
        {
            accessorKey: 'name',
            header: 'Назва моделі',
            size: 250,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>,
        },
        {
            accessorKey: 'brandName',
            header: 'Марка',
            size: 200,
            enableEditing: false,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>,
        },
        {
            id: 'actions',
            header: 'Дії',
            size: 120,
            cell: ({ row }) => {
                const originalRow = originalModelsMap[row.original.id];
                const hasChanged = originalRow && JSON.stringify(originalRow) !== JSON.stringify(row.original);
                return (
                    <div className="action-buttons">
                        <button
                            onClick={() => handleSaveModel(row.original)}
                            className="save-button"
                            disabled={!hasChanged}
                        >
                            Зберегти
                        </button>
                        <button
                            onClick={() => handleDeleteModel(row.original.id)}
                            className="delete-button icon-button"
                            title="Видалити"
                        >
                            ×
                        </button>
                    </div>
                );
            },
        },
    ], [originalModelsMap]);

    const modelsTable = useReactTable({
        data: modelsData,
        columns: modelsColumns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: { pageSize: 50 },
        },
        meta: {
            updateData: (rowIndex, columnId, value) => {
                setModelsData(old =>
                    old.map((row, index) => {
                        if (index === rowIndex) {
                            return { ...row, [columnId]: value };
                        }
                        return row;
                    })
                );
            },
        },
    });

    // ============= Відкриття модалки для редагування =============

    const openEditModal = (cell) => {
        const { row, column } = cell;
        const fieldTypeMap = {
            name: 'text',
        };
        setEditModalData({
            value: cell.getValue(),
            rowIndex: row.index,
            columnId: column.id,
            fieldType: fieldTypeMap[column.id] || 'text',
            fieldLabel: column.columnDef.header,
            brands: activeTab === 'models' ? brandsData : undefined
        });
        setIsModalOpen(true);
    };

    // ============= Функція для рендерингу пагінації =============

    const renderPagination = (table) => (
        <div className="pagination-controls">
            <button onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
                {'<<'}
            </button>
            <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                {'<'}
            </button>
            <span>
                Сторінка{' '}
                <strong>
                    {table.getState().pagination.pageIndex + 1} з {table.getPageCount()}
                </strong>
            </span>
            <input
                type="number"
                defaultValue={table.getState().pagination.pageIndex + 1}
                onChange={e => {
                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                    if (page >= 0 && page < table.getPageCount()) {
                        table.setPageIndex(page);
                    }
                }}
                style={{ width: '60px', margin: '0 5px' }}
                min={1}
                max={table.getPageCount()}
            />
            <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                {'>'}
            </button>
            <button onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>
                {'>>'}
            </button>
            <select
                value={table.getState().pagination.pageSize}
                onChange={e => table.setPageSize(Number(e.target.value))}
                style={{ marginLeft: '10px' }}
            >
                {[10, 20, 30, 40, 50, 100].map(pageSize => (
                    <option key={pageSize} value={pageSize}>
                        Показати {pageSize}
                    </option>
                ))}
            </select>
        </div>
    );

    // ============= Функція для рендерингу таблиці =============

    const renderTable = (table, columns) => (
        <div className="admin-table-container">
            <table className="admin-table">
                <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                                <th key={header.id} style={{ width: header.getSize() ? `${header.getSize()}px` : undefined }}>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(header.column.columnDef.header, header.getContext())}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length} style={{ textAlign: 'center' }}>
                                {activeTab === 'brands' ? 'Немає марок.' : 'Немає моделей.'}
                            </td>
                        </tr>
                    ) : (
                        table.getRowModel().rows.map(row => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map(cell => {
                                    const isEditable = cell.column.id !== 'id' && 
                                                      cell.column.id !== 'actions' && 
                                                      cell.column.id !== 'brandName';
                                    return (
                                        <td
                                            key={cell.id}
                                            style={{ width: cell.column.getSize() ? `${cell.column.getSize()}px` : undefined }}
                                            onClick={isEditable ? () => openEditModal(cell.getContext()) : undefined}
                                            className={isEditable ? 'editable-td' : ''}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );

    // ============= Рендер компонента =============

    if (loading && (brandsData.length === 0 || modelsData.length === 0)) {
        return <div>Завантаження...</div>;
    }

    const handleDeleteAllBrands = async () => {
        if (btoa(deleteAllPassword) !== CONFIRM_HASH) {
            setDeleteAllError('Невірний пароль');
            return;
        }

        setDeleteAllLoading(true);
        setDeleteAllError('');

        try {
            const authToken = localStorage.getItem('authToken');
            if (!authToken) throw new Error('Токен авторизації не знайдено.');

            const response = await fetch(`${BACKEND_URL}/cars/brands/all`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${authToken}` },
            });

            if (!response.ok) {
                const text = await response.text();
                throw new Error(`HTTP ${response.status}: ${text || 'N/A'}`);
            }

            setBrandsData([]);
            setOriginalBrandsMap({});
            setModelsData([]);
            setOriginalModelsMap({});
            setShowDeleteAllModal(false);
            setDeleteAllPassword('');
        } catch (err) {
            setDeleteAllError(`Помилка: ${err.message}`);
        } finally {
            setDeleteAllLoading(false);
        }
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h2 style={{ margin: 0 }}>Керування марками та моделями</h2>
                <button
                    className="delete-all-button"
                    onClick={() => { setShowDeleteAllModal(true); setDeleteAllPassword(''); setDeleteAllError(''); }}
                    type="button"
                >
                    🗑 Видалити всі марки
                </button>
            </div>

            {/* Модальне вікно підтвердження видалення всіх брендів */}
            {showDeleteAllModal && (
                <div className="modal-overlay" onClick={() => setShowDeleteAllModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
                        <h3 style={{ marginTop: 0, color: '#c0392b' }}>⚠️ Видалити всі марки та моделі</h3>
                        <p style={{ color: '#555' }}>
                            Ця дія незворотна. Видаляються всі марки, моделі та відв'язуються товари. Введіть пароль підтвердження.
                        </p>
                        <input
                            type="password"
                            value={deleteAllPassword}
                            onChange={e => { setDeleteAllPassword(e.target.value); setDeleteAllError(''); }}
                            placeholder="Пароль підтвердження"
                            style={{ width: '100%', padding: '8px', marginBottom: '8px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px' }}
                            onKeyDown={e => e.key === 'Enter' && handleDeleteAllBrands()}
                            autoFocus
                        />
                        {deleteAllError && (
                            <p style={{ color: '#c0392b', margin: '0 0 8px', fontSize: '0.9rem' }}>{deleteAllError}</p>
                        )}
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowDeleteAllModal(false)}
                                className="cancel-button"
                                type="button"
                                disabled={deleteAllLoading}
                            >
                                Скасувати
                            </button>
                            <button
                                onClick={handleDeleteAllBrands}
                                className="confirm-delete-button"
                                type="button"
                                disabled={deleteAllLoading || !deleteAllPassword}
                            >
                                {deleteAllLoading ? 'Видалення...' : 'Підтвердити'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Таби для перемикання між Марками та Моделями */}
            <div className="admin-tabs" style={{ marginBottom: '20px', borderBottom: '2px solid #e0e0e0' }}>
                <button
                    onClick={() => setActiveTab('brands')}
                    style={{
                        padding: '10px 20px',
                        marginRight: '10px',
                        border: 'none',
                        background: activeTab === 'brands' ? '#2D6CDF' : '#f5f5f5',
                        color: activeTab === 'brands' ? 'white' : '#333',
                        cursor: 'pointer',
                        borderRadius: '4px 4px 0 0',
                        fontWeight: activeTab === 'brands' ? 'bold' : 'normal',
                    }}
                >
                    Марки автомобілів
                </button>
                <button
                    onClick={() => setActiveTab('models')}
                    style={{
                        padding: '10px 20px',
                        border: 'none',
                        background: activeTab === 'models' ? '#2D6CDF' : '#f5f5f5',
                        color: activeTab === 'models' ? 'white' : '#333',
                        cursor: 'pointer',
                        borderRadius: '4px 4px 0 0',
                        fontWeight: activeTab === 'models' ? 'bold' : 'normal',
                    }}
                >
                    Моделі автомобілів
                </button>
            </div>

            {error && <div className="error-message" style={{ marginBottom: '15px', color: 'red' }}>Помилка: {error}</div>}

            {/* Відображення контенту залежно від активного табу */}
            {activeTab === 'brands' ? (
                <>
                    <div style={{ marginBottom: '15px' }}>
                        <button onClick={openCreateBrandModal} className="create-button">
                            + Створити нову марку
                        </button>
                    </div>
                    {renderTable(brandsTable, brandsColumns)}
                    {renderPagination(brandsTable)}
                </>
            ) : (
                <>
                    <div style={{ marginBottom: '15px' }}>
                        <button onClick={openCreateModelModal} className="create-button">
                            + Створити нову модель
                        </button>
                    </div>
                    {renderTable(modelsTable, modelsColumns)}
                    {renderPagination(modelsTable)}
                </>
            )}

            {/* Модальне вікно */}
            <EditModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                initialValue={editModalData.value}
                onSave={handleModalSave}
                fieldType={editModalData.fieldType}
                fieldLabel={editModalData.fieldLabel}
                brands={editModalData.brands}
            />
        </div>
    );
};

export default CarsAdmin;

